import { useEffect, useMemo, useState } from 'react';
import { coverThumbPath, signRenderPaths } from './deckImages';
import { isPresentationTemplateType } from '../documents/documentMeta';

/**
 * Whether the "master decks" feature (wave 2 deck templates) is on for an account. The flag lives
 * in `accounts.features.master_decks` (or `accounts.settings.features.master_decks`).
 * @param {object|null|undefined} account - Active account row.
 * @returns {boolean} True when on.
 */
export function isMasterDecksEnabled(account) {
  if (!account || typeof account !== 'object') return false;
  return account.features?.master_decks === true || account.settings?.features?.master_decks === true;
}

/** Templates that can have a rendered cover (finished PowerPoint templates). */
const hasCover = (template) => template
  && template.id
  && isPresentationTemplateType(template.templateType)
  && template.status !== 'importing'
  && template.status !== 'processing';

/**
 * Signed URLs of the first rendered slide of each deck template, for the template list cards.
 * Reads only the render status and folder from `generation_metadata` (never `content`), and only when
 * `enabled` (the master decks flag), so other accounts make no extra request.
 * @param {object} args - Arguments.
 * @param {Array<object>} args.templates - Workspace templates.
 * @param {string|null|undefined} args.accountId - Active account id.
 * @param {object} args.client - Supabase client.
 * @param {boolean} [args.enabled] - Run at all.
 * @returns {Object<string, string>} Template id to cover image URL.
 */
export function useDeckCoverThumbnails({ templates, accountId, client, enabled = true }) {
  const key = useMemo(() => {
    if (!enabled || !accountId) return '';
    return (templates || []).filter(hasCover).map((t) => `${t.id}@${t.updatedAt || ''}`).sort().join(',');
  }, [templates, accountId, enabled]);
  const [covers, setCovers] = useState({});

  useEffect(() => {
    if (!key || typeof client?.from !== 'function') return undefined;
    let active = true;
    const ids = key.split(',').map((entry) => entry.split('@')[0]);
    (async () => {
      const { data, error } = await client
        .from('templates')
        .select('id,renderStatus:generation_metadata->>renderStatus,renderPrefix:generation_metadata->>renderPrefix,renderBucket:generation_metadata->>renderBucket')
        .in('id', ids)
        .eq('account_id', accountId);
      if (error) throw error;
      const paths = new Map();
      (data || []).forEach((row) => {
        const path = coverThumbPath(row, accountId);
        if (path) paths.set(row.id, path);
      });
      const urls = await signRenderPaths(client, [...paths.values()]);
      const next = {};
      paths.forEach((path, id) => {
        if (urls.get(path)) next[id] = urls.get(path);
      });
      if (active) setCovers(next);
    })().catch((error) => {
      console.warn('Could not load deck thumbnails:', error);
    });
    return () => {
      active = false;
    };
  }, [key, accountId, client]);

  return key ? covers : {};
}

export default useDeckCoverThumbnails;
